import { supabase } from '../lib/supabase';

/**
 * SEO Service Layer
 * Manages SEO audit items, local citations, keywords, and competitor tracking
 */

// ============================================
// SEO AUDIT ITEMS
// ============================================

/**
 * Get all SEO audit items
 * @param {Object} filters - Optional filters (category, status)
 * @returns {Promise<Array>} List of audit items
 */
export async function getSEOAuditItems(filters = {}) {
  try {
    let query = supabase?.from('seo_audit_items')?.select('*')?.order('priority', { ascending: false })?.order('created_at', { ascending: false });

    if (filters?.category) {
      query = query?.eq('category', filters?.category);
    }

    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching SEO audit items:', error);
    throw error;
  }
}

/**
 * Get SEO audit summary by category
 * @returns {Promise<Array>} Summary statistics
 */
export async function getSEOAuditSummary() {
  try {
    const { data, error } = await supabase?.rpc('get_seo_audit_summary');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching SEO audit summary:', error);
    throw error;
  }
}

/**
 * Update SEO audit item status
 * @param {string} itemId - Audit item ID
 * @param {string} status - New status (passed, warning, failed, pending)
 * @param {string} notes - Optional notes
 * @returns {Promise<Object>} Updated item
 */
export async function updateSEOAuditItem(itemId, status, notes = null) {
  try {
    const updateData = {
      status,
      last_checked_at: new Date()?.toISOString(),
    };

    if (notes) {
      updateData.notes = notes;
    }

    const { data, error } = await supabase?.from('seo_audit_items')?.update(updateData)?.eq('id', itemId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating SEO audit item:', error);
    throw error;
  }
}

// ============================================
// LOCAL CITATIONS
// ============================================

/**
 * Get all local citations
 * @param {boolean} verifiedOnly - Return only verified citations
 * @returns {Promise<Array>} List of citations
 */
export async function getLocalCitations(verifiedOnly = false) {
  try {
    let query = supabase?.from('local_citations')?.select('*')?.order('directory_name');

    if (verifiedOnly) {
      query = query?.eq('is_verified', true);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching local citations:', error);
    throw error;
  }
}

/**
 * Add or update local citation
 * @param {Object} citation - Citation data
 * @returns {Promise<Object>} Created/updated citation
 */
export async function upsertLocalCitation(citation) {
  try {
    const { data, error } = await supabase?.from('local_citations')?.upsert(citation, { onConflict: 'directory_name' })?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error upserting local citation:', error);
    throw error;
  }
}

// ============================================
// LOCAL KEYWORDS
// ============================================

/**
 * Get all local keywords
 * @param {string} location - Filter by target location
 * @returns {Promise<Array>} List of keywords
 */
export async function getLocalKeywords(location = null) {
  try {
    let query = supabase?.from('local_keywords')?.select('*')?.order('search_volume', { ascending: false });

    if (location) {
      query = query?.eq('target_location', location);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching local keywords:', error);
    throw error;
  }
}

/**
 * Update keyword ranking
 * @param {string} keywordId - Keyword ID
 * @param {number} currentRanking - New current ranking
 * @returns {Promise<Object>} Updated keyword
 */
export async function updateKeywordRanking(keywordId, currentRanking) {
  try {
    const { data, error } = await supabase?.from('local_keywords')?.update({ 
        current_ranking: currentRanking,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', keywordId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating keyword ranking:', error);
    throw error;
  }
}

// ============================================
// COMPETITORS
// ============================================

/**
 * Get all SEO competitors
 * @returns {Promise<Array>} List of competitors
 */
export async function getSEOCompetitors() {
  try {
    const { data, error } = await supabase?.from('seo_competitors')?.select('*')?.order('local_ranking');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching SEO competitors:', error);
    throw error;
  }
}

// ============================================
// ANALYTICS EVENTS
// ============================================

/**
 * Track custom analytics event
 * @param {string} eventName - Event name
 * @param {Object} eventData - Event data
 * @returns {Promise<Object>} Created event
 */
export async function trackAnalyticsEvent(eventName, eventData = {}) {
  try {
    const event = {
      event_name: eventName,
      event_category: eventData?.category || null,
      event_label: eventData?.label || null,
      event_value: eventData?.value || null,
      page_path: window.location?.pathname,
      event_data: eventData,
    };

    const { data, error } = await supabase?.from('analytics_events')?.insert(event)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error tracking analytics event:', error);
    throw error;
  }
}

// ============================================
// SCHEMA MARKUP UTILITIES
// ============================================

/**
 * Generate LocalBusiness schema markup for Sacramento County
 * @param {Object} businessInfo - Business information
 * @returns {Object} JSON-LD schema
 */
export function generateLocalBusinessSchema(businessInfo = {}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': businessInfo?.url || 'https://sailorsbail.com',
    name: businessInfo?.name || '3D Sailors Bail Bonds',
    image: businessInfo?.image || 'https://sailorsbail.com/logo.png',
    url: businessInfo?.url || 'https://sailorsbail.com',
    telephone: businessInfo?.phone || '(916) 555-0100',
    priceRange: '$',
    address: {
      '@type': 'PostalAddress',
      streetAddress: businessInfo?.street || '123 Main Street',
      addressLocality: businessInfo?.city || 'Sacramento',
      addressRegion: 'CA',
      postalCode: businessInfo?.zip || '95814',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: businessInfo?.latitude || 38.5816,
      longitude: businessInfo?.longitude || -121.4944,
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '00:00',
      closes: '23:59',
    },
    areaServed: [
      {
        '@type': 'City',
        name: 'Sacramento',
        '@id': 'https://en.wikipedia.org/wiki/Sacramento,_California',
      },
      {
        '@type': 'AdministrativeArea',
        name: 'Sacramento County',
      },
      {
        '@type': 'AdministrativeArea',
        name: 'Placer County',
      },
      {
        '@type': 'AdministrativeArea',
        name: 'El Dorado County',
      },
    ],
    knowsAbout: ['Bail Bonds', 'Emergency Bail', 'Sacramento County Jail', '24 Hour Bail Service'],
  };
}

/**
 * Generate FAQ schema markup
 * @param {Array} faqs - Array of {question, answer} objects
 * @returns {Object} JSON-LD schema
 */
export function generateFAQSchema(faqs = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs?.map(faq => ({
      '@type': 'Question',
      name: faq?.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq?.answer,
      },
    })),
  };
}

/**
 * Inject schema markup into page head
 * @param {Object} schema - JSON-LD schema object
 */
export function injectSchemaMarkup(schema) {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(schema);
  document.head?.appendChild(script);
}

// ============================================
// SERVICE OBJECT EXPORT
// ============================================

/**
 * SEO Service Object
 * Provides unified interface for all SEO operations
 */
export const seoService = {
  // Audit Items
  getAuditItems: getSEOAuditItems,
  getAuditSummary: getSEOAuditSummary,
  updateAuditItem: updateSEOAuditItem,
  
  // Local Citations
  getCitations: getLocalCitations,
  upsertCitation: upsertLocalCitation,
  
  // Keywords
  getKeywords: getLocalKeywords,
  updateKeywordRanking: updateKeywordRanking,
  
  // Competitors
  getCompetitors: getSEOCompetitors,
  
  // Analytics
  trackEvent: trackAnalyticsEvent,
  
  // Schema Markup (mock implementation for now)
  getSchemaMarkup: async (filters = {}) => {
    try {
      // This would fetch from a schema_markup table if it exists
      // For now, return empty array with proper structure
      return { data: [], error: null };
    } catch (error) {
      console.error('Error fetching schema markup:', error);
      return { data: [], error };
    }
  },
  
  // Local Performance (mock implementation for now)
  getLocalPerformance: async (filters = {}) => {
    try {
      // This would fetch from a local_performance table if it exists
      // For now, return empty array with proper structure
      return { data: [], error: null };
    } catch (error) {
      console.error('Error fetching local performance:', error);
      return { data: [], error };
    }
  },
  
  // Analytics Summary (mock implementation for now)
  getAnalyticsSummary: async (filters = {}) => {
    try {
      // This would fetch from an analytics_summary table if it exists
      // For now, return empty data with proper structure
      return { data: null, error: null };
    } catch (error) {
      console.error('Error fetching analytics summary:', error);
      return { data: null, error };
    }
  },
  
  // Schema utilities
  generateLocalBusinessSchema,
  generateFAQSchema,
  injectSchemaMarkup,
};